"use client";

import { FC, useState } from "react";
import dynamic from "next/dynamic";
import BFLLazyLoad from "@repo/shared-ui/BFLLazyLoad";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import BFLToggleButtonWrapper from "@repo/shared-ui/BFLToggleButtonWrapper";
import BFLToggleButton from "@repo/shared-ui/BFLToggleButton";
import {
  IBalanceSheet,
  ICashFlow,
  ICompetition,
  IKeyRatio,
  IRevenue,
  IStockFinancialsData,
  RawItem,
  SubTabItem,
} from "../../interfaces/StockFinancials.interface";
import {
  BAR_COLORS,
  LEGEND_KEY_MAP,
  LEGEND_TITLE_KEYS,
  MODAL_KEY,
  OPTION_KEY_MAP,
  STOCK_FINANCIALS_TABS,
  SUB_TAB_TITLE_KEYS,
  TABLE_COLUMN,
  TABLE_ROW,
  TAB_KEY_MAP,
} from "./StockFinancials.constants";
import StockFinancialsTable from "./StockFinancialsTable";
import StockFinancialsCompetitionTable from "./StockFinancialsCompetitionTable";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import { IScripfinancialsContent } from "../../interfaces/ITradingMallContentV1.interface";
import { handleFinancialsTabDL } from "./BFLTabsFunction";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";
import StockFinancialsModal from "./StockFinancialsModal";

const StockFinancialsGraph: any = dynamic(
  () => import("./StockFinancialsGraph.tsx") as any,
  { ssr: false, loading: () => <BFLSkeleton classes="mb-2" height={389} /> },
);

interface StockFinancialsTabsProps {
  content: IScripfinancialsContent;
  stockFinancialsData: IStockFinancialsData | null;
  competitionData: ICompetition[];
}

const StockFinancialsTabs: FC<StockFinancialsTabsProps> = ({
  content,
  stockFinancialsData,
  competitionData,
}) => {
  const loginStatus = useAppSelector(selectLoginStatus);

  const [selectedTab, setSelectedTab] =
    useState<keyof IStockFinancialsData["financials"]>("keyResults");

  const getDisplayTitle = (
    item: SubTabItem,
    keys: readonly string[],
  ): string | null => {
    for (const key of keys) {
      if (item?.[key]) return item[key]!;
    }
    return null;
  };
  const getOptionsForTab = (
    tab: keyof IStockFinancialsData["financials"],
  ): string[] => {
    const tabKey = TAB_KEY_MAP[tab];
    const rawItems: any = Array.isArray(
      content?.[tabKey as keyof IScripfinancialsContent],
    )
      ? content?.[tabKey as keyof IScripfinancialsContent]
      : [];

    return rawItems.flatMap((obj: RawItem) =>
      Object.values(obj)
        .map((item) => getDisplayTitle(item, SUB_TAB_TITLE_KEYS))
        .filter(Boolean),
    );
  };

  const [selectedOption, setSelectedOption] = useState(
    () => getOptionsForTab(selectedTab)[0] ?? "",
  );

  const [periodData, setPeriodData] = useState<
    IRevenue[] | IKeyRatio[] | IBalanceSheet[] | ICashFlow[] | ICompetition[]
  >(() => {
    const keys = OPTION_KEY_MAP[selectedOption] ?? [];
    return (
      keys?.reduce((acc, key) => {
        const tabData = (stockFinancialsData?.financials as any)?.[selectedTab];
        return (
          acc ||
          (tabData && key in tabData
            ? (tabData as Record<string, any>)[key]
            : undefined)
        );
      }, undefined) || []
    );
  });

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(() => {
    const firstItem = periodData?.[0];
    return firstItem && "date" in firstItem ? String(firstItem.date) : null;
  });

  const getModalContent = (tabKey: string) => {
    const modalKey = MODAL_KEY[tabKey as keyof typeof MODAL_KEY];
    if (!modalKey || !content) return "";

    const contentValue = (content as unknown as Record<string, unknown>)[
      modalKey
    ];
    return contentValue ? String(contentValue) : "";
  };

  const modalContent = getModalContent(selectedTab);

  const handleTabOrOptionChange = (
    tab: keyof IStockFinancialsData["financials"],
    option: string,
  ) => {
    const keys = OPTION_KEY_MAP[option] ?? [];
    let newData:
      | IRevenue[]
      | IKeyRatio[]
      | IBalanceSheet[]
      | ICashFlow[]
      | ICompetition[] = [];

    for (const key of keys) {
      const tabData = stockFinancialsData?.financials?.[tab];
      if (tabData && typeof tabData === "object" && key in tabData) {
        const dataValue = (tabData as unknown as Record<string, unknown>)[key];
        if (Array.isArray(dataValue)) {
          newData = dataValue as
            | IRevenue[]
            | IKeyRatio[]
            | IBalanceSheet[]
            | ICashFlow[]
            | ICompetition[];
          break;
        }
      }
    }

    setPeriodData(newData);

    let shouldReset = true;
    if (selectedPeriod && newData.length > 0) {
      shouldReset = !newData.some(
        (item) => "date" in item && String(item.date) === selectedPeriod
      );
    }
    if (shouldReset) {
      const firstItem = newData.length > 0 ? newData[0] : null;
      setSelectedPeriod(
        firstItem && "date" in firstItem ? String(firstItem.date) : null
      );
    }
  };

  const handleTabChange = (tab: keyof IStockFinancialsData["financials"]) => {
    const tabPosition =
      STOCK_FINANCIALS_TABS.findIndex((t) => t.value === tab) + 1;
    const tabLabel =
      STOCK_FINANCIALS_TABS.find((t) => t.value === tab)?.label || tab;

    handleFinancialsTabDL(loginStatus, {
      sectionTitle: "Stock Financials",
      ctaText: tabLabel,
      tabPosition,
    });

    setSelectedTab(tab);

    const tabOptions = getOptionsForTab(tab);
    const firstOption = tabOptions[0] ?? "";
    setSelectedOption(firstOption);

    handleTabOrOptionChange(tab, firstOption);
  };

  const handleOptionChange = (option: string) => {
    setSelectedOption(option);
    handleTabOrOptionChange(selectedTab, option);
  };

  const RenderOptions = () => (
    <BFLToggleButtonWrapper>
      {getOptionsForTab(selectedTab).map((option: string, index: number) => (
        <BFLToggleButton
          key={option + "-toggle-" + index}
          active={selectedOption === option}
          onClick={() => handleOptionChange(option)}
        >
          {option}
        </BFLToggleButton>
      ))}
    </BFLToggleButtonWrapper>
  );

  const Legend = ({
    tab,
  }: {
    tab?: keyof IStockFinancialsData["financials"];
  }) => {
    const legendTab = tab ?? selectedTab;
    const legendKey =
      legendTab in LEGEND_KEY_MAP
        ? LEGEND_KEY_MAP[legendTab as keyof typeof LEGEND_KEY_MAP]
        : undefined;
    const currentContent = legendKey
      ? content?.[legendKey as keyof IScripfinancialsContent]
      : undefined;

    return (
      <div className="mt-4 flex w-full items-center justify-between">
        <span className="text-sm leading-4 text-gray-900 md:text-base">
          {legendTab !== "keyRatios" && "(Values in Cr)"}
        </span>
        <div className="flex items-center space-x-4">
          {Array.isArray(currentContent) &&
            currentContent[0] &&
            Object.entries(currentContent[0]).map(([key, item], index) => {
              const displayText =
                getDisplayTitle(item as SubTabItem, LEGEND_TITLE_KEYS) || key;

              return (
                <div key={key} className="flex items-center space-x-2">
                  <span
                    className="h-3 w-3 rounded-[2px]"
                    style={{
                      backgroundColor:
                        BAR_COLORS[index % BAR_COLORS.length] ?? "#000000",
                    }}
                  ></span>
                  <span className="text-xs md:text-base">{displayText}</span>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="mt-3" data-component-id="stock-financials">
        <BFLTabs
          tabs={STOCK_FINANCIALS_TABS}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          tabClassName="mr-3 md:mr-4 pb-[1px]"
          gapClassName="md:pb-[14px]"
        />
        <div className="border-lgray-200 bottom-0 z-0 w-full border-b"></div>
        <div className="min-h[70px] flex justify-between">
          <RenderOptions />
          <div className={selectedTab !== "competition" ? "block" : "hidden"}>
            <StockFinancialsModal content={modalContent} />
          </div>

          {Object.keys(MODAL_KEY).map((tabKey) => {
            if (tabKey === selectedTab || tabKey === "competition") return null;
            const tabModalContent = getModalContent(tabKey);
            if (!tabModalContent) return null;

            return (
              <div
                key={`seo-modal-wrapper-${tabKey}`}
                className="invisible absolute -left-[9999px] -top-[9999px] h-px w-px overflow-hidden opacity-0"
              >
                <StockFinancialsModal content={tabModalContent} />
              </div>
            );
          })}
        </div>
      </div>

      {STOCK_FINANCIALS_TABS.filter((tab) => tab.value !== "competition").map(
        (tab) => {
          const tabOption =
            selectedTab === tab.value
              ? selectedOption
              : (getOptionsForTab(tab.value)[0] ?? "");
          const keys = OPTION_KEY_MAP[tabOption] ?? [];
          let tabPeriodData:
            | IRevenue[]
            | IKeyRatio[]
            | IBalanceSheet[]
            | ICashFlow[] = [];
          for (const key of keys) {
            const tabData = stockFinancialsData?.financials?.[tab.value];
            if (tabData && typeof tabData === "object" && key in tabData) {
              const dataValue = (tabData as unknown as Record<string, unknown>)[
                key
              ];
              if (Array.isArray(dataValue)) {
                tabPeriodData = dataValue as
                  | IRevenue[]
                  | IKeyRatio[]
                  | IBalanceSheet[]
                  | ICashFlow[];
                break;
              }
            }
          }

          return getOptionsForTab(tab.value).map((option) => {
            const keys = OPTION_KEY_MAP[option] ?? [];
            let tabPeriodData:
              | IRevenue[]
              | IKeyRatio[]
              | IBalanceSheet[]
              | ICashFlow[] = [];
            for (const key of keys) {
              const tabData = stockFinancialsData?.financials?.[tab.value];
              if (tabData && typeof tabData === "object" && key in tabData) {
                const dataValue = (
                  tabData as unknown as Record<string, unknown>
                )[key];
                if (Array.isArray(dataValue)) {
                  tabPeriodData = dataValue as
                    | IRevenue[]
                    | IKeyRatio[]
                    | IBalanceSheet[]
                    | ICashFlow[];
                  break;
                }
              }
            }
            // For the active tab/option, use selectedPeriod from state; otherwise fallback to first item
            const isActive = selectedTab === tab.value && selectedOption === option;
            const tabSelectedPeriod = isActive
              ? selectedPeriod
              : (tabPeriodData.length > 0 && tabPeriodData[0] && "date" in tabPeriodData[0]
                  ? String((tabPeriodData[0] as any).date)
                  : null);
            const tabSelectedBars =
              tabPeriodData.length > 0
                ? (() => {
                    const firstItem = tabPeriodData[0];
                    const chartdata =
                      firstItem && "chartdata" in firstItem
                        ? (firstItem as any).chartdata
                        : {};
                    return Object.keys(chartdata || {}).map((key, i) => ({
                      key: `chartdata.${key}`,
                      name: key,
                      color: BAR_COLORS[i % BAR_COLORS.length],
                    }));
                  })()
                : [];
            return (
              <div
                key={tab.value + "-" + option}
                style={{
                  display:
                    isActive
                      ? "block"
                      : "none",
                }}
              >
                <Legend />
                <div style={{ display: "none" }} aria-hidden="true">
                  {STOCK_FINANCIALS_TABS.filter(
                    (tab) => tab.value !== "competition",
                  ).map((tab) => (
                    <div key={tab.value + "-legend-seo"}>
                      <Legend
                        tab={
                          tab.value as keyof IStockFinancialsData["financials"]
                        }
                      />
                    </div>
                  ))}
                </div>
                <BFLLazyLoad
                  className="min-h-[280px] md:min-h-[389px]"
                  threshold={0.3}
                >
                  <StockFinancialsGraph
                    graphData={tabPeriodData}
                    selectedPeriod={tabSelectedPeriod ?? ""}
                    setSelectedPeriod={(p: string | null) =>
                      setSelectedPeriod(p ? String(p) : null)
                    }
                    dataKeys={tabSelectedBars}
                  />
                </BFLLazyLoad>
                
                {tabPeriodData.map((periodItem, periodIndex) => {
                  const periodDate = "date" in periodItem ? String(periodItem.date) : null;
                  const isActivePeriod = periodDate === tabSelectedPeriod;
                  
                  return (
                    <div
                      key={`${tab.value}-${option}-period-${periodDate || periodIndex}`}
                      style={{ display: isActivePeriod ? "block" : "none" }}
                    >
                      <StockFinancialsTable
                        header={
                          Array.isArray(
                            content?.[
                              TABLE_COLUMN[tab.value] as keyof IScripfinancialsContent
                            ],
                          )
                            ? (content[
                                TABLE_COLUMN[
                                  tab.value
                                ] as keyof IScripfinancialsContent
                              ] as any)
                            : []
                        }
                        columns={
                          Array.isArray(
                            content?.[
                              TABLE_ROW[tab.value] as keyof IScripfinancialsContent
                            ],
                          )
                            ? (content[
                                TABLE_ROW[tab.value] as keyof IScripfinancialsContent
                              ] as any)
                            : []
                        }
                        stockFinancials={
                          tabPeriodData as unknown as
                            | IKeyRatio[]
                            | IBalanceSheet[]
                            | ICashFlow[]
                            | IRevenue[]
                        }
                        selectedPeriod={periodDate ?? ""}
                      />
                    </div>
                  );
                })}
              </div>
            );
          });
        },
      )}

      <div
        data-ssr-competition="true"
        className={selectedTab === "competition" ? "block" : "hidden"}
      >
        <div className={selectedOption === "Standalone" ? "block" : "hidden"}>
        <StockFinancialsCompetitionTable
          header={content?.tableColumnDetailsCT || []}
          stockFinancials={
            selectedTab === "competition" && periodData.length > 0
              ? (periodData as ICompetition[])
              : competitionData
          }
        />
        </div>
         <div className={selectedOption === "Consolidated" ? "block" : "hidden"}>
        <StockFinancialsCompetitionTable
          header={content?.tableColumnDetailsCT || []}
          stockFinancials={
            selectedTab === "competition" && periodData.length > 0
              ? (periodData as ICompetition[])
              : competitionData
          }
        />
        </div>
      </div>
    </>
  );
};

export default StockFinancialsTabs;
